import {
  QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { api } from 'src/services/api';
import {
  useGetServiceRequestInfiniteList,
  useGetServiceRequestList,
} from './useGetServiceRequestList';
import { ServiceRequest, Tag } from 'src/models';
import { showToastError } from 'src/utilities/errortoast';
import { useGetServiceRequestDetails } from './useGetServiceRequestDetails';

export type TagServiceRequestPayload = {
  serviceRequestId: string;
  tag: Tag;
};

const addTagToServiceRequestClient = ({
  queryClient,
  serviceRequestId,
  tag,
}: {
  queryClient: QueryClient;
  serviceRequestId: string;
  tag: Tag;
}) => {
  const scanServiceRequest = (sr: ServiceRequest) => {
    if (sr.service_request_id === serviceRequestId) {
      return {
        ...sr,
        tags: [...(sr.tags || []).filter((t) => t.id !== tag.id), tag],
      };
    }
    return sr;
  };

  queryClient.setQueriesData<
    ReturnType<typeof useGetServiceRequestList>['data']
  >({ queryKey: ['serviceRequestList'] }, (oldData) => {
    if (!oldData?.serviceRequests?.length) return oldData;

    return {
      ...oldData,
      serviceRequests: oldData.serviceRequests.map(scanServiceRequest),
    };
  });

  queryClient.setQueriesData<
    ReturnType<typeof useGetServiceRequestInfiniteList>['data']
  >({ queryKey: ['serviceRequestList', 'infinite'] }, (oldData) => {
    if (!oldData?.pages?.length) return oldData;

    const newPages = oldData.pages.map((page) => {
      return {
        ...page,
        serviceRequests: page.serviceRequests.map(scanServiceRequest),
      };
    });

    return {
      ...oldData,
      pages: newPages,
    };
  });

  queryClient.setQueriesData<
    ReturnType<typeof useGetServiceRequestDetails>['data']
  >({ queryKey: ['serviceRequest'] }, (oldData) => {
    if (!oldData) return oldData;

    return scanServiceRequest(oldData);
  });
};

const removeTagFromServiceRequestClient = ({
  queryClient,
  serviceRequestId,
  tag,
}: {
  queryClient: QueryClient;
  serviceRequestId: string;
  tag: Tag;
}) => {
  const scanServiceRequest = (sr: ServiceRequest) => {
    if (sr.service_request_id === serviceRequestId) {
      return {
        ...sr,
        tags: (sr.tags || []).filter((t) => t.id !== tag.id),
      };
    }
    return sr;
  };

  queryClient.setQueriesData<
    ReturnType<typeof useGetServiceRequestList>['data']
  >({ queryKey: ['serviceRequestList'] }, (oldData) => {
    if (!oldData?.serviceRequests?.length) return oldData;

    return {
      ...oldData,
      serviceRequests: oldData.serviceRequests.map(scanServiceRequest),
    };
  });

  queryClient.setQueriesData<
    ReturnType<typeof useGetServiceRequestInfiniteList>['data']
  >({ queryKey: ['serviceRequestList', 'infinite'] }, (oldData) => {
    if (!oldData?.pages?.length) return oldData;

    const newPages = oldData.pages.map((page) => {
      return {
        ...page,
        serviceRequests: page.serviceRequests.map(scanServiceRequest),
      };
    });
    return { ...oldData, pages: newPages };
  });

  queryClient.setQueriesData<
    ReturnType<typeof useGetServiceRequestDetails>['data']
  >({ queryKey: ['serviceRequest'] }, (oldData) => {
    if (!oldData) return oldData;

    return scanServiceRequest(oldData);
  });
};

export const useTagServiceRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['tagServiceRequest'],
    mutationFn: ({ serviceRequestId, tag }: TagServiceRequestPayload) => {
      addTagToServiceRequestClient({
        queryClient,
        serviceRequestId,
        tag,
      });
      return api.post(`/v3/service_request/${serviceRequestId}/tag`, {
        tag_id: tag.id,
      });
    },
    onError: (_, { serviceRequestId, tag }) => {
      showToastError(`Failed to add tag ${tag.name}`);
      removeTagFromServiceRequestClient({
        queryClient,
        serviceRequestId,
        tag,
      });
    },
  });
};

export const useUntagServiceRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['untagServiceRequest'],
    mutationFn: ({ serviceRequestId, tag }: TagServiceRequestPayload) => {
      removeTagFromServiceRequestClient({
        queryClient,
        serviceRequestId,
        tag,
      });
      return api.delete(`/v3/service_request/${serviceRequestId}/tag`, {
        data: {
          tag_id: tag.id,
        },
      });
    },
    onError: (_, { serviceRequestId, tag }) => {
      showToastError(`Failed to remove tag ${tag.name}`);
      addTagToServiceRequestClient({
        queryClient,
        serviceRequestId,
        tag,
      });
    },
  });
};
